import {
  ChevronDown,
  ExternalLink,
  Linkedin,
  MessageCircle,
  Search,
  ThumbsUp,
  User,
} from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedInSearchPosts } from 'sprites-types/workflow/tools/linkedinSearchPosts'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function PostItem({ post, className }: { post: any; className?: string }) {
  const authorName = post.author?.name || post.authorName || post.user?.name || 'LinkedIn User'
  const authorHeadline = post.author?.headline || post.authorHeadline || post.user?.title || ''
  const content = post.content || post.text || post.body || post.description || ''
  const timestamp = post.timestamp || post.publishedAt || post.createdAt || post.date
  const likes = post.likes || post.likeCount || post.reactions || 0
  const comments = post.comments || post.commentCount || post.numComments || 0
  const shares = post.shares || post.shareCount || post.reposts || 0
  const postUrl = post.url || post.link || post.permalink
  const authorProfilePic = post.author?.profilePic || post.authorProfilePic || post.user?.avatar
  const mediaUrl = post.media?.url || post.imageUrl || post.videoUrl

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return dateStr
    }
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="mb-3 flex items-start gap-3">
          {authorProfilePic ? (
            <Image
              src={authorProfilePic}
              alt={`${authorName} profile`}
              width={40}
              height={40}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <User className="h-5 w-5 text-blue-600" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">{authorName}</div>
            {authorHeadline && <div className="mb-1 text-xs text-gray-600">{authorHeadline}</div>}
            {timestamp && <div className="text-xs text-gray-500">{formatDate(timestamp)}</div>}
          </div>
        </div>

        {content && (
          <div className="mb-3 line-clamp-4 whitespace-pre-wrap text-xs text-gray-700">
            {content.length > 300 ? `${content.slice(0, 300)}...` : content}
          </div>
        )}

        {mediaUrl && (
          <div className="mb-3 overflow-hidden rounded-2xl bg-gray-100">
            <Image
              src={mediaUrl}
              alt="Post media"
              width={400}
              height={240}
              className="max-h-60 w-full object-cover"
            />
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-gray-600">
            {likes > 0 && (
              <div className="flex items-center gap-1">
                <ThumbsUp className="h-3 w-3 text-blue-500" />
                <span>{formatNumber(likes)}</span>
              </div>
            )}
            {comments > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3 text-gray-500" />
                <span>{formatNumber(comments)}</span>
              </div>
            )}
            {shares > 0 && <div className="text-gray-500">{formatNumber(shares)} shares</div>}
          </div>
          {postUrl && (
            <a
              href={postUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="h-3 w-3" />
              View Post
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function LinkedinSearchPostsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedInSearchPosts, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.keywords || 'search'
  const posts = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.posts ||
      (result as any)?.results ||
      (result ? [result] : [])
  const totalEngagement = posts.reduce((sum: number, p: any) => {
    const likes = p.likes || p.likeCount || p.reactions || 0
    const comments = p.comments || p.commentCount || p.numComments || 0
    const shares = p.shares || p.shareCount || p.reposts || 0
    return sum + likes + comments + shares
  }, 0)

  if (posts.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No posts found</div>
            <div className="text-xs text-gray-600">Search: &quot;{query}&quot;</div>
          </div>
        </div>
      </div>
    )
  }

  if (posts.length === 1) {
    return <PostItem post={posts[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Linkedin className="h-5 w-5 text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                LinkedIn Post Search
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {posts.length} posts • &quot;{query}&quot; • {totalEngagement.toLocaleString()}{' '}
                total engagement
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {posts.map((post: any, index: number) => (
            <PostItem key={post.id || post.url || index} post={post} className="mx-8" />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadLinkedinSearchPosts(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinSearchPostsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedInSearchPosts, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.keywords || 'search'
  const posts = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.posts ||
      (result as any)?.results ||
      (result ? [result] : [])

  if (posts.length === 0) {
    return `## LinkedIn Post Search

**Query:** "${query}"

No posts found.

---
`
  }

  const tableHeader = `| Author | Date | Likes | Comments | Shares | Content | Link |
|--------|------|-------|----------|--------|---------|------|`

  const tableRows = posts
    .map((post: any) => {
      const authorName = post.author?.name || post.authorName || post.user?.name || 'LinkedIn User'
      const timestamp = post.timestamp || post.publishedAt || post.createdAt || post.date
      const date = timestamp ? new Date(timestamp).toLocaleDateString() : '-'
      const likes = post.likes || post.likeCount || post.reactions || 0
      const likesDisplay = likes > 0 ? likes.toLocaleString() : '-'
      const comments = post.comments || post.commentCount || post.numComments || 0
      const commentsDisplay = comments > 0 ? comments.toLocaleString() : '-'
      const shares = post.shares || post.shareCount || post.reposts || 0
      const sharesDisplay = shares > 0 ? shares.toLocaleString() : '-'
      const content = (post.content || post.text || post.body || post.description || '-')
        .replace(/\|/g, '\\|')
        .replace(/\n/g, ' ')
        .slice(0, 80)
      const contentDisplay = content.length === 80 ? content + '...' : content
      const postUrl = post.url || post.link || post.permalink
      const linkDisplay = postUrl ? `[View](${postUrl})` : '-'

      return `| ${authorName} | ${date} | ${likesDisplay} | ${commentsDisplay} | ${sharesDisplay} | ${contentDisplay} | ${linkDisplay} |`
    })
    .join('\n')

  const detailedPosts = posts
    .filter(
      (post: any) => (post.content || post.text || post.body || post.description)?.length > 80
    )
    .slice(0, 5) // Limit to first 5 detailed posts
    .map((post: any) => {
      const authorName = post.author?.name || post.authorName || post.user?.name || 'LinkedIn User'
      const content = post.content || post.text || post.body || post.description || ''
      const timestamp = post.timestamp || post.publishedAt || post.createdAt || post.date
      const date = timestamp ? new Date(timestamp).toLocaleDateString() : ''
      return `### ${authorName}${date ? ` - ${date}` : ''}\n\n${content}`
    })
    .join('\n\n')

  return `## LinkedIn Post Search (${posts.length} posts)

**Query:** "${query}"

${tableHeader}
${tableRows}

${detailedPosts ? `\n## Detailed Posts\n\n${detailedPosts}\n` : ''}
---
`
}

export const LinkedinSearchPostsDisplay = createWorkflowToolComponent(
  LinkedinSearchPostsDisplayComponent,
  downloadLinkedinSearchPosts,
  linkedinSearchPostsToMarkdown
)
